<template>
  <div class="accreditations-container">
    <el-row :gutter="20">
      <el-col :xs="24" :sm="24" :md="12" :lg="8" v-for="item in accreditations" :key="item._id">
        <div class="accreditation-card">
          <div class="card-header" :class="{ 'inactive': !item.isActive }">
    <div class="status-badge" :class="{ 'inactive': !item.isActive }">
      {{ item.isActive ? 'Active' : 'Inactive' }}
    </div>
    <h3 class="title">{{ item.accreditation.name }}</h3>
    <div class="description-container">
      <p class="description" :class="{ 'expanded': isExpanded[item._id] }">
        {{ item.accreditation.description }}
      </p>
      <button v-if="shouldShowExpand(item.accreditation.description)"
              class="expand-button"
              @click="toggleExpand(item._id)">
        {{ isExpanded[item._id] ? 'Show less' : 'Show more' }}
      </button>
    </div>
  </div>

          <div class="card-body">
    <div class="info-grid">
      <!-- Required fields (always shown) -->
      <div class="info-item">
        <i class="el-icon-date"></i>
        <div class="info-content">
          <label>Expiry Date</label>
          <span>{{ moment(item.expiry).format('YYYY-MM-DD') }}</span>
        </div>
      </div>

      <div class="info-item">
        <i class="el-icon-time"></i>
        <div class="info-content">
          <label>Date Issued</label>
          <span>{{ moment(item.createdAt).format('YYYY-MM-DD') }}</span>
        </div>
      </div>

      <!-- Expandable section for additional fields -->
      <el-collapse v-if="item.accreditation.additionalFields?.length" class="additional-fields-collapse">
        <el-collapse-item>
          <template slot="title">
            <span class="expand-title">Additional Fields</span>
          </template>
          <div class="info-item" v-for="field in item.accreditation.additionalFields"
               :key="field.label">
            <i class="el-icon-info"></i>
            <div class="info-content">
              <label>{{ field.label }}</label>
              <span>{{ item.meta?.[field.label] || 'N/A' }}</span>
            </div>
          </div>
        </el-collapse-item>
      </el-collapse>
    </div>

          <div class="card-actions">
            <el-tooltip content="View Documents" placement="top">
              <el-button type="text" icon="el-icon-document"
                         @click="openDialog(item)">
              </el-button>
            </el-tooltip>

            <el-tooltip content="Update Fields" placement="top">
              <el-button type="text" icon="el-icon-edit"
                         @click="openMetaDialog(item)">
              </el-button>
            </el-tooltip>

            <el-tooltip content="Card Options" placement="top">
              <el-dropdown @command="downloadAccreditationCard" trigger="click">
                <el-button type="text" icon="el-icon-more" ></el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item :command="{value: item, type:'pdf'}">
                    <i class="el-icon-document"></i> Download as PDF
                  </el-dropdown-item>
                  <el-dropdown-item :command="{value: item, type:'png'}">
                    <i class="el-icon-picture"></i> Download as PNG
                  </el-dropdown-item>
                  <el-dropdown-item :command="{value: item, type:'qr-code'}">
                    <i class="el-icon-postcard"></i> Show QR Code
                  </el-dropdown-item>
                  <el-dropdown-item :command="{value: item, type:'regenerate'}">
                    <i class="el-icon-refresh"></i> Regenerate Card
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </el-tooltip>
          </div>
        </div>
        </div>
      </el-col>
    </el-row>

    <el-dialog :visible.sync="dialogVisible" width="100%">
      <accreditation-files
        :value="selectedAccreditation"
        :current-member="member"
        @close="dialogVisible = false"
      />
    </el-dialog>
    <el-dialog
      :title="`Qr Code for accreditation card`"
      :visible.sync="showQrCodeDialog"
      width="30%"
      >
      <div style=" overflow-x: auto; width:100%">
        <div class="d-flex justify-content-center">
          <img :src="qrCode" alt="QR Code" />
        </div>
      </div>
    </el-dialog>
    <el-dialog
  :visible.sync="metaDialogVisible"
  :title="`Update Fields - ${selectedAccreditation?.accreditation?.name || ''}`"
  width="50%"
  :close-on-click-modal="false"
>
  <el-form
    ref="metaForm"
    :model="selectedAccreditation"
    label-position="top"
    v-if="selectedAccreditation"
  >
    <el-row :gutter="20">
      <el-col
        v-for="field in selectedAccreditation.accreditation.additionalFields"
        :key="field.label"
        :xs="24"
        :sm="field.type === 'boolean' ? 12 : 24"
        :md="field.type === 'boolean' ? 12 : 12"
      >
        <el-form-item
          :label="field.label"
        >
          <!-- Initialize meta object if it doesn't exist -->
          <template v-if="!selectedAccreditation.meta">
            {{ $set(selectedAccreditation, 'meta', {}) }}
          </template>

          <el-input
            v-if="field.type === 'text'"
            v-model="selectedAccreditation.meta[field.label]"
            :placeholder="`Enter ${field.label}`"
            @change="updateAccreditationMeta(selectedAccreditation)"
          />

          <el-input-number
            v-else-if="field.type === 'number'"
            v-model="selectedAccreditation.meta[field.label]"
            :controls="true"
            :placeholder="`Enter ${field.label}`"
            @change="updateAccreditationMeta(selectedAccreditation)"
            style="width: 100%"
          />

          <el-radio-group
            v-else-if="field.type === 'boolean'"
            v-model="selectedAccreditation.meta[field.label]"
            @change="updateAccreditationMeta(selectedAccreditation)"
          >
            <el-radio :label="true">Yes</el-radio>
            <el-radio :label="false">No</el-radio>
          </el-radio-group>

          <el-date-picker
            v-else-if="field.type === 'date'"
            v-model="selectedAccreditation.meta[field.label]"
            type="date"
            value-format="YYYY-MM-DD"
            :placeholder="`Select ${field.label}`"
            @change="updateAccreditationMeta(selectedAccreditation)"
            style="width: 100%"
          />

          <el-select
            v-else-if="field.type === 'select'"
            v-model="selectedAccreditation.meta[field.label]"
            :placeholder="`Select ${field.label}`"
            @change="updateAccreditationMeta(selectedAccreditation)"
            style="width: 100%"
          >
            <el-option
              v-for="option in field.options"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
        </el-form-item>
      </el-col>
    </el-row>
  </el-form>
  <span slot="footer" class="dialog-footer">
    <el-button @click="metaDialogVisible = false">Cancel</el-button>
    <el-button type="primary" @click="closeMetaDialog">Confirm</el-button>
  </span>
</el-dialog>
  </div>
</template>

<script>
import _, { capitalize } from 'lodash';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts.js';
import moment from 'moment';
import AccreditationFiles from './AccreditationFiles.vue';

export default {
  props: ['member'],
  data() {
    return {
      capitalize,
      accreditations: [],
      dialogVisible: false,
      selectedAccreditation: null,
      showQrCodeDialog: false,
      qrCode: null,
      metaDialogVisible: false,
      isExpanded: {},
    };
  },
  components: {
    AccreditationFiles
  },
  mounted() {
    if (this.member._id) {
      this.$store.commit('loading', { loading: true, text: 'Loading...' });
      this.$http
        .get(
          `/assemble/api/v1/portal/members/${this.member._id}/accreditations`
        )
        .then((res) => {
          this.accreditations = res.data.data.map((accreditation) => {
          // Initialize meta if it doesn't exist
            if (!accreditation.meta) {
              this.$set(accreditation, 'meta', {});
            }
            return accreditation;
          });
          console.log('accreditations', this.accreditations);
          this.$store.commit('loading', false);
        })
        .catch((e) => {
          this.$message.error(e.response.data.message);
          this.$store.commit('loading', false);
        });
    }
  },
  methods: {
    shouldShowExpand(text) {
      const lineHeight = 20; // Approximate line height in pixels
      const maxHeight = lineHeight * 2; // Two lines
      const tempEl = document.createElement('div');
      tempEl.style.width = '100%';
      tempEl.style.position = 'absolute';
      tempEl.style.visibility = 'hidden';
      tempEl.style.fontSize = '0.9em';
      tempEl.innerText = text;
      document.body.appendChild(tempEl);
      const height = tempEl.offsetHeight;
      document.body.removeChild(tempEl);
      return height > maxHeight;
    },
    toggleExpand(id) {
      this.$set(this.isExpanded, id, !this.isExpanded[id]);
    },
    openMetaDialog(accreditation) {
      this.selectedAccreditation = accreditation;
      if (accreditation.accreditation.additionalFields?.length) {
        this.metaDialogVisible = true;
      } else {
        this.$message.error('No additional fields to update');
      }
    },

    closeMetaDialog() {
      this.metaDialogVisible = false;
      this.selectedAccreditation = null;
    },
    async updateAccreditationMeta(accreditation) {
      try {
        this.$store.commit('loading', true);
        await this.$http.put(
          `/assemble/api/v1/portal/accreditations/${accreditation._id}/meta`,
          { meta: accreditation.meta }
        );
        this.$message.success('Additional fields updated successfully');

        // If you have an accreditation card that needs to be regenerated
        // after meta updates, you can trigger that here
        this.handleAccreditationCardNotFound(
          { value: accreditation, type: 'update' },
          'update',
          true
        );
      } catch (error) {
        this.$message.error(error.response?.data?.message || 'Failed to update additional fields');
      } finally {
        this.$store.commit('loading', false);
      }
    },
    parseDate(dateStr) {
      if (!dateStr) return null;
      // Convert from DD/MM/YYYY to Date object
      const [day, month, year] = dateStr.split('/');
      return new Date(year, month - 1, day);
    },

    updateDate(row, fieldLabel, date) {
    // Convert Date object to DD/MM/YYYY string
      const formattedDate = date ? moment(date).format('DD/MM/YYYY') : null;
      this.$set(row.meta, fieldLabel, formattedDate);
      this.updateAccreditationMeta(row);
    },
    isTimestamp(value) {
      // Check if the value is a number and has 13 digits (milliseconds timestamp)
      // or 10 digits (seconds timestamp)
      return typeof value === 'number'
        && (String(value).length === 13 || String(value).length === 10);
    },

    formatTimestamp(timestamp) {
      // Convert to milliseconds if it's in seconds
      const ms = String(timestamp).length === 10 ? timestamp * 1000 : timestamp;
      return moment(ms).format('YYYY-MM-DD HH:mm:ss');
    },
    openDialog(accreditation) {
      this.selectedAccreditation = accreditation;
      this.dialogVisible = true;
    },
    splitTextIntoLines(ctx, text, maxWidth) {
      const words = text.split(' ');
      const lines = [];
      let currentLine = words[0];

      for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const { width } = ctx.measureText(`${currentLine} ${word}`);

        if (width < maxWidth) {
          currentLine += ` ${word}`;
        } else {
          lines.push(currentLine);
          currentLine = word;
        }
      }

      lines.push(currentLine);
      return lines;
    },
    async generateMSCard(membershipDetails, memberProfilePics, entityPic) {
      const cardCanvas = document.createElement('canvas');
      cardCanvas.width = 500;
      cardCanvas.height = 300;
      const cardWidth = cardCanvas.width;
      const pageHeight = cardCanvas.height;
      const cardContext = cardCanvas.getContext('2d');
      const lineSpacing = 22;
      const columnSpacing = 260;
      // const lineWidth = 2;
      let isLogoURL = false;
      let isProfileURL = false;
      let isEntityURL = false;

      // Modern color scheme
      const colors = {
        primary: '#1a237e', // Deep blue for primary elements
        secondary: '#303F9F', // Lighter blue for secondary elements
        text: '#2c3e50', // Dark blue-grey for text
        lightText: '#5c6b7f', // Lighter text color
        accent: '#3949ab', // Accent color for highlights
        divider: '#e0e6ed', // Light grey for dividers
        background: '#ffffff' // White background
      };

      let logoImage;
      let profileImage;
      let entityImage;
      let logoPromise;
      let profilePromise;
      let entityPromise;
      const dobFormat = +process.env.VUE_APP_NATIONAL_ID === 15 ? 'YYYY' : 'DD/MM/YYYY'; // Add this line

      // Load Logo Image
      if (process.env.VUE_APP_LOGO && process.env.VUE_APP_LOGO !== '') {
        isLogoURL = true;
      }
      if (memberProfilePics && memberProfilePics !== '') {
        isProfileURL = true;
      }
      if (entityPic && entityPic !== '') {
        isEntityURL = true;
      }

      const additionalFields = membershipDetails.additionalFields || [];
      console.log('additionalFields', additionalFields);

      const memberMeta = membershipDetails.memberMeta || {};

      if (isLogoURL) {
        const logoContent = await this.$http.post('/assemble/api/v1/portal/registrations/image-content', { logo: process.env.VUE_APP_LOGO, isURL: true });
        logoImage = new Image();
        logoImage.src = logoContent.data.data.imageContent;
        // eslint-disable-next-line no-return-assign
        logoPromise = new Promise((resolve) => logoImage.onload = resolve);
      }
      if (isProfileURL) {
        const profileContent = await this.$http.post('/assemble/api/v1/portal/registrations/image-content', { memberProfile: memberProfilePics, isURL: true });
        profileImage = new Image();
        profileImage.src = profileContent.data.data.memberImageContent;
        // eslint-disable-next-line no-return-assign
        profilePromise = new Promise((resolve) => profileImage.onload = resolve);
      }
      if (isEntityURL) {
        const entityContent = await this.$http.post('/assemble/api/v1/portal/registrations/image-content', { entityLogo: entityPic, isURL: true });
        entityImage = new Image();
        entityImage.src = entityContent.data.data.entityImageContent;
        // eslint-disable-next-line no-return-assign
        entityPromise = new Promise((resolve) => entityImage.onload = resolve);
      }

      await Promise.all([logoPromise, profilePromise, entityPromise]);

      // Draw card background with subtle gradient
      const gradient = cardContext.createLinearGradient(0, 0, cardWidth, pageHeight);
      gradient.addColorStop(0, colors.background);
      gradient.addColorStop(1, '#f8f9fa');

      cardContext.fillStyle = gradient;
      cardContext.beginPath();
      // Increased border radius for modern look
      const borderRadius = 25;
      cardContext.moveTo(borderRadius, 0);
      cardContext.lineTo(cardWidth - borderRadius, 0);
      cardContext.quadraticCurveTo(cardWidth, 0, cardWidth, borderRadius);
      cardContext.lineTo(cardWidth, pageHeight - borderRadius);
      cardContext.quadraticCurveTo(cardWidth, pageHeight, cardWidth - borderRadius, pageHeight);
      cardContext.lineTo(borderRadius, pageHeight);
      cardContext.quadraticCurveTo(0, pageHeight, 0, pageHeight - borderRadius);
      cardContext.lineTo(0, borderRadius);
      cardContext.quadraticCurveTo(0, 0, borderRadius, 0);
      cardContext.closePath();
      cardContext.fill();

      // Add subtle shadow effect
      cardContext.beginPath();
      cardContext.moveTo(borderRadius, 0);
      cardContext.lineTo(cardWidth - borderRadius, 0);
      cardContext.quadraticCurveTo(cardWidth, 0, cardWidth, borderRadius);
      cardContext.lineTo(cardWidth, pageHeight - borderRadius);
      cardContext.quadraticCurveTo(cardWidth, pageHeight, cardWidth - borderRadius, pageHeight);
      cardContext.lineTo(borderRadius, pageHeight);
      cardContext.quadraticCurveTo(0, pageHeight, 0, pageHeight - borderRadius);
      cardContext.lineTo(0, borderRadius);
      cardContext.quadraticCurveTo(0, 0, borderRadius, 0);
      cardContext.closePath();
      cardContext.strokeStyle = colors.primary; // or '#000000' for black border
      cardContext.lineWidth = 2;
      cardContext.stroke();

      cardContext.imageSmoothingEnabled = true;
      cardContext.imageSmoothingQuality = 'high';

      // Draw member profile logo
      const profileLogoWidth = 100;
      const profileLogoHeight = 100;
      const profileLogoX = 10;
      const profileLogoY = 10;
      cardContext.globalAlpha = 1;
      if (profileImage) {
        cardContext.drawImage(profileImage, profileLogoX, profileLogoY, profileLogoWidth, profileLogoHeight);
      } else {
        cardContext.fillStyle = '#f5f5f5';
        cardContext.fillRect(profileLogoX, profileLogoY, profileLogoWidth, profileLogoHeight);
      }

      // Draw entity logo
      const entityLogoWidth = 100;
      const entityLogoHeight = 100;
      const entityLogoX = cardWidth - entityLogoWidth - 10;
      const entityLogoY = 10;

      if (entityImage) {
        cardContext.drawImage(entityImage, entityLogoX, entityLogoY, entityLogoWidth, entityLogoHeight);
      } else {
        cardContext.drawImage(logoImage, entityLogoX, entityLogoY, entityLogoWidth, entityLogoHeight);
      }

      // Draw entity text
      const maxLineWidth = 260;
      cardContext.font = 'bold 22px "Helvetica Neue", Arial, sans-serif';
      cardContext.fillStyle = colors.primary;
      const textStartX = profileLogoX + profileLogoWidth + 10;
      const entityTextY = profileLogoY + 20;

      const firstLevelLines = this.splitTextIntoLines(cardContext, membershipDetails.entityCreatedFirstLevelLimited, maxLineWidth);
      firstLevelLines.slice(0, 2).forEach((line, index) => {
        cardContext.fillText(line, textStartX, entityTextY + (index * 25));
      });

      const secondLevelStartY = entityTextY + (firstLevelLines.length * 25) + 5;
      cardContext.font = '16px "Helvetica Neue", Arial, sans-serif';
      cardContext.fillStyle = colors.lightText;

      if (membershipDetails.entityCreatedSecondLevelLimited) {
        const secondLevelLines = this.splitTextIntoLines(cardContext, membershipDetails.entityCreatedSecondLevelLimited, maxLineWidth);
        secondLevelLines.slice(0, 2).forEach((line, index) => {
          cardContext.fillText(line, textStartX, secondLevelStartY + (index * 20));
        });
      }

      // Draw accreditation name and description
      const accreditationY = pageHeight - 165;
      cardContext.fillStyle = colors.primary;
      cardContext.font = 'bold 22px "Helvetica Neue", Arial, sans-serif';
      const accreditationNameLines = this.splitTextIntoLines(cardContext, membershipDetails.accreditationName, 450);
      accreditationNameLines.forEach((line, index) => {
        cardContext.fillText(line, 10, accreditationY + index * lineSpacing);
      });

      // Add description
      cardContext.font = '16px "Helvetica Neue", Arial, sans-serif';
      cardContext.fillStyle = colors.lightText;
      const description = membershipDetails.description || '';
      const truncatedDescription = description.length > 60 ? `${description.substring(0, 57)}...` : description;
      cardContext.fillText(truncatedDescription, 10, accreditationY + lineSpacing);

      // Draw sections with modern styling
      const sectionHeaderY = pageHeight - 100;
      cardContext.font = '600 18px "Helvetica Neue", Arial, sans-serif';
      cardContext.fillStyle = colors.primary;
      cardContext.fillText('Accreditation Details', 10, sectionHeaderY);
      cardContext.fillText('Member Details', columnSpacing, sectionHeaderY);

      // Add subtle section dividers
      cardContext.beginPath();
      cardContext.moveTo(10, sectionHeaderY + 5);
      cardContext.lineTo(240, sectionHeaderY + 5);
      cardContext.moveTo(columnSpacing, sectionHeaderY + 5);
      cardContext.lineTo(cardWidth - 10, sectionHeaderY + 5);
      cardContext.strokeStyle = colors.divider;
      cardContext.lineWidth = 2;
      cardContext.stroke();

      // Draw details
      const detailsStartY = sectionHeaderY + lineSpacing;
      cardContext.font = '16px "Helvetica Neue", Arial, sans-serif';

      // First column - Accreditation Details
      cardContext.fillStyle = colors.lightText;
      cardContext.fillText('Expiry:', 10, detailsStartY);
      cardContext.fillStyle = colors.text;
      cardContext.fillText(membershipDetails.expiry, 10 + cardContext.measureText('Expiry: ').width, detailsStartY);

      // Additional fields
      let currentDetailY = detailsStartY + lineSpacing;
      additionalFields.forEach((field) => {
        if (field.displayOnCard) {
          const fieldValue = memberMeta[field.name] || memberMeta[field.label] || '';
          if (currentDetailY < pageHeight - 30) {
            cardContext.fillStyle = colors.lightText;
            cardContext.fillText(`${field.label}:`, 10, currentDetailY);
            cardContext.fillStyle = colors.text;
            cardContext.fillText(fieldValue.toString(), 10 + cardContext.measureText(`${field.label}: `).width, currentDetailY);
            currentDetailY += lineSpacing;
          }
        }
      });

      // Second column - Member Details
      const memberDetails = [
        { label: 'Name', value: membershipDetails.memberName },
        { label: 'Member ID', value: this.member._id },
        { label: 'Member Number', value: membershipDetails.memberNumber },
        {
          label: dobFormat === 'YYYY' ? 'Year of Birth' : 'DOB',
          value: moment(membershipDetails.memberDob).format(dobFormat)
        }
      ];

      memberDetails.forEach((detail, index) => {
        cardContext.fillStyle = colors.lightText;
        cardContext.fillText(`${detail.label}:`, columnSpacing, detailsStartY + index * lineSpacing);
        cardContext.fillStyle = colors.text;
        cardContext.fillText(detail.value,
          columnSpacing + cardContext.measureText(`${detail.label}: `).width,
          detailsStartY + index * lineSpacing);
      });

      // Vertical divider with gradient
      const dividerGradient = cardContext.createLinearGradient(0, sectionHeaderY, 0, currentDetailY);
      dividerGradient.addColorStop(0, colors.divider);
      dividerGradient.addColorStop(1, '#ffffff00');
      cardContext.beginPath();
      cardContext.moveTo(columnSpacing - 20, sectionHeaderY);
      cardContext.lineTo(columnSpacing - 20, currentDetailY);
      cardContext.strokeStyle = dividerGradient;
      cardContext.lineWidth = 1;
      cardContext.stroke();

      // Add subtle watermark
      cardContext.globalAlpha = 0.03;
      cardContext.drawImage(logoImage, 0, 0, cardWidth, pageHeight);
      cardContext.globalAlpha = 1;

      return new Promise((resolve) => {
        cardCanvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/png');
      });
    },
    handleAccreditationCardNotFound(val, action, change) {
      const { value: accreditation } = val;
      let msCard;
      let confirmText = 'Accreditation card is not available yet. Do you want to generate it now?';
      if (change) {
        confirmText = 'Accreditation details have been updated. Select yes to generate a new Accreditation Card.';
      }
      if (action === 'regenerate') {
        confirmText = 'Do you want to regenerate the Accreditation Card?';
      }
      this.$confirm(confirmText, 'Confirm', {
        distinguishCancelAndClose: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
      })
        .then(async () => {
          console.log('accreditationss', accreditation);
          const currMember = this.member;
          const currentSeason = this.$store.getters['season/currentSeason'];
          const currMemberProfilePic = _.get(currMember, 'meta.imgUrl');
          const entityPic = _.get(accreditation, 'regoEntity.meta.imgUrl');
          const { orgtree } = accreditation.accreditation;
          console.log('orgtree', orgtree);
          console.log('currMember', currMember);

          // Manually create an array in the desired order from lowest to highest
          const orderedOrgtree = [
            orgtree.club, // Start with the lowest level
            orgtree.association,
            orgtree.state,
            orgtree.national
          ].filter((org) => org !== undefined); // This will remove any undefined levels, in case some levels do not exist
          console.log('orderedOrgtree', orderedOrgtree);

          const entityCreatedFirstLevel = orderedOrgtree[0] ? orderedOrgtree[0].name : '';
          const entityCreatedSecondLevel = orderedOrgtree[1] ? orderedOrgtree[1].name : '';
          const entityCreatedFirstLevelLimited = entityCreatedFirstLevel.length > 67 ? `${entityCreatedFirstLevel.substring(0, 67)}...` : entityCreatedFirstLevel;
          const entityCreatedSecondLevelLimited = entityCreatedSecondLevel.length > 77 ? `${entityCreatedSecondLevel.substring(0, 77)}...` : entityCreatedSecondLevel;

          // Reverse the array to start from club and then move up, and take the first two entities
          // const entityCreated = orderedOrgtree.slice(0, 2).map((org) => org.name).join(', ');
          // console.log('entityCreated length', entityCreated.length);
          // const entityCreatedLimited = entityCreated.length > 45 ? `${entityCreated.substring(0, 45)}...` : entityCreated;
          const accreditationDetails = {
            accreditationName: accreditation.fromImport ? 'Legacy Accreditation' : accreditation.accreditation.name,
            accreditationId: accreditation.fromImport ? '0' : accreditation._id,
            expiry: moment(_.get(accreditation, 'expiry')).format('DD/MM/YYYY'),
            memberType: _.capitalize(accreditation.memberType),
            season: currentSeason,
            memberId: this.member._id,
            memberName: `${currMember.profile.firstName} ${currMember.profile.lastName}`,
            memberDob: currMember.profile.dob,
            memberNumber: _.get(currMember, 'meta.memberNumber', ''),
            entityCreatedFirstLevelLimited,
            entityCreatedSecondLevelLimited,
            deregistered: accreditation.deregistered,
            expired: accreditation.expired,
            additionalFields: accreditation.accreditation.additionalFields,
            memberMeta: accreditation.meta,
            description: accreditation.accreditation.description,
          };
          console.log('accreditationDetails', accreditationDetails);

          this.$store.commit('loading', true);
          msCard = await this.generateMSCard(accreditationDetails, currMemberProfilePic, entityPic);
          console.log('card', msCard);
          console.log('accreditationDetails', accreditationDetails);
          // get presigned url
          const presignedUrl = await this.$http.post('/assemble/api/v1/portal/accreditation-card/presigned-url',
            {
              fileName: `accreditation-card-${accreditation._id}-${this.member._id}.png`,
              folder: 'accreditation-cards',
              accreditationDetails,
              accreditationId: accreditation._id,
              regenerating: !!change,
            });

          const { url, s3Path } = presignedUrl.data.data;
          this.$store.commit('loading', false);

          if (s3Path) {
            _.assign(accreditation, { accreditationCard: s3Path });
          }

          await fetch(url, {
            method: 'PUT',
            body: msCard,
            headers: { 'Content-Type': '*/*' },
          });
          this.$message.success('Membership card generated successfully');
          if (action === 'showQRCode') {
            const findMsCard = await this.$http.get(`/assemble/api/v1/portal/accreditations/${accreditation._id}/members/${accreditation.member._id}/accreditation-card`);
            if (_.get(findMsCard, 'data.msCard.meta.qrCode')) {
              this.qrCode = findMsCard.data.msCard.meta.qrCode;
              this.showQrCodeDialog = true;
            }
          }
          if (msCard) {
            this.downloadAccreditationCard(val, true);
          }
        })
        .catch(async () => {
          if (change) {
            await this.downloadAccreditationCard(val, true);
          }
        });
    },
    dataURItoBlob(dataURI) {
      const byteString = atob(dataURI.split(',')[1]);
      const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);

      for (let i = 0; i < byteString.length; i += 1) {
        ia[i] = byteString.charCodeAt(i);
      }

      return new Blob([ab], { type: mimeString });
    },
    async getImageBase64(url) {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        };
        img.src = url;
      });
    },
    async downloadAccreditationCard(val, skipCheck) {
      // console.log(type);
      const { type, value: accreditation } = val;
      console.log('accreditation', accreditation);
      console.log('memberId', this.member._id);
      if (!(skipCheck === true)) {
        const msCardRes = await this.$http.get(`/assemble/api/v1/portal/accreditations/${accreditation._id}/members/${this.member._id}/accreditation-card`);
        this.msCard = msCardRes.data.data;
        const msCardDetails = msCardRes.data.msCard;
        console.log('msCardDetails', msCardDetails);
        if (msCardDetails?.meta?.qrCode) {
          this.qrCode = msCardDetails.meta.qrCode;
        }
        const hasMsCard = this.msCard && this.msCard !== '';
        if (!hasMsCard) {
          this.handleAccreditationCardNotFound(val);
          return;
        }
        if (type === 'regenerate') {
          this.handleAccreditationCardNotFound(val, 'regenerate');
          return;
        }
        if (msCardDetails) {
          // check meta field newChange
          if (msCardDetails.meta.newChange) {
            this.handleAccreditationCardNotFound(val, 'download', msCardDetails.meta.lastChange);
            return;
          }
        }
      }
      console.log('msCard', this.msCard);

      // msCard is in base64 format already
      if (type === 'png') {
        const link = document.createElement('a');
        link.download = `${accreditation._id}.png`;

        // Convert to blob with base64
        const blob = this.dataURItoBlob(this.msCard);
        const blobUrl = URL.createObjectURL(blob);
        // Set the link href to the blob url
        link.href = blobUrl;
        // Append the link to the body
        document.body.appendChild(link);
        // Trigger the click event on the link
        link.click();
      } else if (type === 'pdf') {
        pdfMake.vfs = pdfFonts.pdfMake.vfs;
        const dd = {
          content: [
            { image: this.msCard, width: [400], alignment: 'center' },
          ],
        };
        pdfMake.createPdf(dd).download(`${accreditation._id}.pdf`);
      } else if (type === 'qr-code') {
        this.showQrCodeDialog = true;
        console.log('this.msCard', this.msCard);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.accreditations-container {
  padding: 20px;

  .accreditation-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    margin-bottom: 20px;
    transition: transform 0.2s, box-shadow 0.2s;
    overflow: hidden;
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 250px;
    width: 100%;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    }

    .card-header {
  padding: 20px;
  background: linear-gradient(135deg, var(--highlight-color), var(--secondary-color));
  color: white;
  position: relative;
  height: 160px; // Fixed height for header
  display: flex;
  flex-direction: column;

  &.inactive {
    background: linear-gradient(135deg, #9e9e9e, #616161);
  }

  .status-badge {
    position: absolute;
    top: 20px;
    right: 20px;
    padding: 4px 12px;
    border-radius: 20px;
    background: #4CAF50;
    font-size: 0.8em;
    font-weight: 500;

    &.inactive {
      background: #f44336;
    }
  }

  .title {
    margin: 0 0 8px;
    font-size: 1.4em;
    font-weight: 600;
    line-height: 1.2;
    height: 34px; // Fixed height for title
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .description-container {
    position: relative;
    flex-grow: 1;

    .description {
      margin: 0;
      font-size: 0.9em;
      line-height: 20px; // Fixed line height
      opacity: 0.9;
      display: -webkit-box;
      -webkit-line-clamp: 2; // Show 2 lines by default
      -webkit-box-orient: vertical;
      overflow: hidden;
      transition: all 0.3s ease;

      &.expanded {
        -webkit-line-clamp: unset; // Remove line limit when expanded
      }
    }

    .expand-button {
      position: absolute;
      bottom: -20px;
      right: 0;
      background: none;
      border: none;
      color: white;
      font-size: 0.8em;
      padding: 2px 6px;
      cursor: pointer;
      opacity: 0.8;
      transition: opacity 0.2s;

      &:hover {
        opacity: 1;
      }

      &::before {
        content: '';
        position: absolute;
        right: 100%;
        width: 30px;
        height: 100%;
        background: linear-gradient(to right, transparent, var(--secondary-color));
      }
    }
  }
}

    .card-body {
      padding: 20px 20px 0 20px;
      flex: 1;

      .info-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 16px;

        .info-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;

          i {
            color: var(--highlight-color);
            font-size: 1.2em;
            margin-top: 3px;
          }

          .info-content {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            width: 100%;

            label {
              font-size: 0.8em;
              color: #666;
              margin-bottom: 4px;
            }

            span {
              color: #333;
              font-weight: 500;
            }
          }
        }
      }

      .additional-fields-collapse {
  width: 100%;
  margin-top: 10px;
  border: none;

  ::v-deep .el-collapse-item__header {
    color: var(--highlight-color);
    font-weight: 500;
    border: none;
    padding: 0;

    &:hover {
      color: var(--secondary-color);
    }
  }

  ::v-deep .el-collapse-item__content {
    padding: 10px 0;
  }

  .expand-title {
    display: flex;
    align-items: center;
    gap: 8px;

  }
}
    }
    .card-actions {
  // padding: 15px 20px;
  margin-top: 10px;
  border-top: 1px solid #eee;
  background: #f8f9fa;
  min-height: 60px;

  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(32px, max-content));
  gap: 12px;
  justify-content: space-between;
  align-items: center;

  .el-button {
    &[circle] {
      width: 32px;
      height: 32px;
      padding: 0;
      border: 1px solid #dcdfe6;

      &:hover {
        background: var(--highlight-color);
        border-color: var(--highlight-color);
        color: white;
      }
    }
  }

  .el-dropdown {
    .el-button {
      font-size: 16px;
    }
  }
}

::v-deep .el-dropdown-menu {
  .el-dropdown-menu__item {
    display: grid;
    grid-template-columns: 16px 1fr;
    gap: 8px;
    align-items: center;

    i {
      font-size: 16px;
    }
  }
}

  }
}

// Make columns same height
.el-row {
  display: flex;
  flex-wrap: wrap;

  .el-col {
    display: flex;
    margin-bottom: 20px;
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .accreditations-container {
    padding: 10px;

    .accreditation-card {
      .card-body {
        .info-grid {
          grid-template-columns: 1fr;
        }
      }

      .card-actions {
        flex-direction: column;
        gap: 8px;

        .el-button {
          width: 100%;
        }
      }
    }
  }
}
</style>
